import React, { useState, useEffect, useRef } from "react";
import Modal from "../modal/Modal";
import { toast } from "react-toastify";
import { LockClosedIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { profileService } from "../../service/profileService";
import { OrgAdminProfile, OrgUserProfile, User } from "../../types/api";
import { useAuth } from "../../auth/AuthProvider";

interface EditProfileProps {
  isOpen: boolean;
  onClose: () => void;
  orgUser: OrgUserProfile | null;
  user: User | null;
  orgAdmin: OrgAdminProfile | null;
  onProfileUpdate?: (updatedData: {
    fullName: string;
    mobileNumber?: string;
    department?: string;
    organizationName?: string;
    adminTitle?: string;
  }) => void;
}

const EditProfile: React.FC<EditProfileProps> = ({
  isOpen,
  onClose,
  orgUser,
  user,
  orgAdmin,
  onProfileUpdate,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    department: "",
    organizationName: "",
    tenantName: "",
    employeeId: "",
    firstName: "",
    lastName: "",
    supervisor: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const {
    userType,
    refreshUserData,
    setUser,
    setOrgAdmin,
    setOrgUser,
    loading: authLoading,
  } = useAuth();

  useEffect(() => {
    if (user && isOpen && !authLoading) {
      const isOrgAdmin = userType === "orgadmin";
      const isOrgUser = userType === "orguser";

      const newFormData = {
        fullName: isOrgAdmin
          ? (user as any).org_admin_name || ""
          : isOrgUser
            ? `${(user as any).firstName || ""} ${
                (user as any).lastName || ""
              }`.trim()
            : user.tenant_name || "",
        email: isOrgAdmin ? (user as any).username || "" : user.email || "",
        mobileNumber: isOrgAdmin
          ? (user as any).org_admin_phone || ""
          : (user as any).mobile || "",
        department: isOrgAdmin
          ? ""
          : isOrgUser
            ? orgUser?.department || ""
            : "",
        organizationName: isOrgAdmin ? (user as any).org_name || "" : "",
        tenantName: isOrgAdmin ? user.tenant_name || "" : "",
        employeeId: isOrgUser
          ? (user as any).employeeId || orgUser?.employeeId || ""
          : "",
        firstName: isOrgUser ? (user as any).firstName || "" : "",
        lastName: isOrgUser ? (user as any).lastName || "" : "",
        supervisor: isOrgUser
          ? (user as any).supervisor || orgUser?.supervisor || ""
          : "",
      };

      setFormData(newFormData);
      setHasUnsavedChanges(false);
      setDataLoaded(true);
    } else if (isOpen && authLoading) {
      setDataLoaded(false);
    }
  }, [user, orgUser, orgAdmin, isOpen, userType, authLoading]);

  // Refresh user data once per modal open to avoid repeated calls
  const hasRefreshedOnOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !hasRefreshedOnOpenRef.current) {
      hasRefreshedOnOpenRef.current = true;
      (async () => {
        try {
          await refreshUserData();
        } catch (error) {
          console.error("Failed to refresh user data:", error);
        }
      })();
    }
    if (!isOpen) {
      hasRefreshedOnOpenRef.current = false;
    }
  }, [isOpen, refreshUserData]);

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors };

    switch (name) {
      case "fullName":
      case "firstName":
      case "lastName":
        if (!value.trim()) {
          newErrors[name] = `${
            name === "fullName"
              ? "Administrator name"
              : name === "firstName"
                ? "First name"
                : "Last name"
          } is required`;
        } else if (value.trim().length < 2) {
          newErrors[name] = `${
            name === "fullName"
              ? "Administrator name"
              : name === "firstName"
                ? "First name"
                : "Last name"
          } must be at least 2 characters`;
        } else if (value.trim().length > 50) {
          newErrors[name] = `${
            name === "fullName"
              ? "Administrator name"
              : name === "firstName"
                ? "First name"
                : "Last name"
          } must not exceed 50 characters`;
        } else {
          delete newErrors[name];
        }
        break;
      case "organizationName":
        if (userType === "orgadmin" && !value.trim()) {
          newErrors.organizationName = "Organization name is required";
        } else if (value && value.trim().length > 100) {
          newErrors.organizationName =
            "Organization name must not exceed 100 characters";
        } else {
          delete newErrors.organizationName;
        }
        break;
      case "employeeId":
        if (value && value.trim().length > 20) {
          newErrors.employeeId = "Employee ID must not exceed 20 characters";
        } else {
          delete newErrors.employeeId;
        }
        break;
      case "mobileNumber":
        if (value && value.trim()) {
          const digitsOnly = value.replace(/\D/g, "");
          if (digitsOnly.length < 10 || digitsOnly.length > 15) {
            newErrors.mobileNumber =
              "Phone number must be between 10-15 digits";
          } else if (!/^[\+]?[\d\s\-\(\)]{10,20}$/.test(value)) {
            newErrors.mobileNumber = "Please enter a valid phone number format";
          } else {
            delete newErrors.mobileNumber;
          }
        } else {
          delete newErrors.mobileNumber;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      validateField(key, formData[key as keyof typeof formData]);
    });

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the validation errors");
      return;
    }

    setLoading(true);
    try {
      const isOrgAdmin = userType === "orgadmin";
      const isOrgUser = userType === "orguser";

      let updatedUserData: any;

      if (isOrgAdmin) {
        const orgAdminUpdateData = {
          org_admin_name: formData.fullName.trim(),
          org_name: formData.organizationName.trim() || undefined,
          org_admin_phone: formData.mobileNumber.trim() || undefined,
        };

        await profileService.updateOrgAdminProfile(orgAdminUpdateData);

        // Update formData immediately
        setFormData((prev) => ({
          ...prev,
          fullName: orgAdminUpdateData.org_admin_name || prev.fullName,
          organizationName:
            orgAdminUpdateData.org_name || prev.organizationName,
          mobileNumber: orgAdminUpdateData.org_admin_phone || prev.mobileNumber,
        }));

        // Update AuthProvider state
        updatedUserData = {
          ...user,
          org_admin_name: orgAdminUpdateData.org_admin_name,
          org_name: orgAdminUpdateData.org_name,
          org_admin_phone: orgAdminUpdateData.org_admin_phone,
        };
        setOrgAdmin(updatedUserData);
        setUser(updatedUserData);

        if (onProfileUpdate) {
          onProfileUpdate({
            fullName: formData.fullName,
            mobileNumber: formData.mobileNumber,
            organizationName: formData.organizationName,
            adminTitle: formData.tenantName,
          });
        }
      } else if (isOrgUser) {
        const orgUserUpdateData = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          mobile: formData.mobileNumber.trim() || undefined,
          employeeId: formData.employeeId.trim() || undefined,
          supervisor: formData.supervisor.trim() || undefined,
        };

        await profileService.updateOrgUserProfile(orgUserUpdateData);

        // Update formData immediately
        setFormData((prev) => ({
          ...prev,
          firstName: orgUserUpdateData.firstName || prev.firstName,
          lastName: orgUserUpdateData.lastName || prev.lastName,
          mobileNumber: orgUserUpdateData.mobile || prev.mobileNumber,
          employeeId: orgUserUpdateData.employeeId || prev.employeeId,
          supervisor: orgUserUpdateData.supervisor || prev.supervisor,
        }));

        // Update AuthProvider state
        updatedUserData = {
          ...user,
          firstName: orgUserUpdateData.firstName,
          lastName: orgUserUpdateData.lastName,
          mobile: orgUserUpdateData.mobile,
          employeeId: orgUserUpdateData.employeeId,
          supervisor: orgUserUpdateData.supervisor,
        };
        setOrgUser(updatedUserData);
        setUser(updatedUserData);

        if (onProfileUpdate) {
          onProfileUpdate({
            fullName: `${formData.firstName} ${formData.lastName}`.trim(),
            mobileNumber: formData.mobileNumber,
            department: formData.department,
          });
        }
      } else {
        const updateData = {
          fullName: formData.fullName.trim(),
          mobileNumber: formData.mobileNumber.trim() || undefined,
          department: formData.department.trim() || undefined,
        };

        await profileService.updateProfile(updateData);

        // Update formData immediately
        setFormData((prev) => ({
          ...prev,
          fullName: updateData.fullName || prev.fullName,
          mobileNumber: updateData.mobileNumber || prev.mobileNumber,
          department: updateData.department || prev.department,
        }));

        // Update AuthProvider state
        updatedUserData = {
          ...user,
          tenant_name: updateData.fullName,
          mobile: updateData.mobileNumber,
          department: updateData.department,
        };
        setUser(updatedUserData);

        if (onProfileUpdate) {
          onProfileUpdate(updateData);
        }
      }

      // Refresh user data from server to ensure consistency
      await refreshUserData();

      toast.success("Profile updated successfully!");
      setHasUnsavedChanges(false);
      onClose();
    } catch (error: any) {
      console.error("Profile update failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update profile. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        setHasUnsavedChanges(false);
        setErrors({});
        onClose();
      }
    } else {
      setErrors({});
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        if (Object.keys(errors).length === 0 && !loading) {
          const form = document.getElementById(
            "edit-profile-form",
          ) as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, errors, loading]);

  if (!isOpen) return null;

  const getDisplayTitle = () => {
    if (userType === "orgadmin") {
      return "Organization Administrator";
    } else if (userType === "orguser") {
      return orgUser?.employeeId || formData.employeeId || "Organization User";
    } else {
      return "Others";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeButton={false}
      modalClass="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 relative overflow-hidden"
    >
      <div>
        {(authLoading || !dataLoaded) && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading profile data...</span>
          </div>
        )}
        {!authLoading && dataLoaded && (
          <>
            <div className="flex justify-between items-start pt-6 px-6 mb-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Edit Profile
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {getDisplayTitle()}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form id="edit-profile-form" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3.5 px-6 pb-4">
                {userType === "orgadmin" ? (
                  <>
                    <div>
                      <label
                        htmlFor="organizationName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.organizationName
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter organization name"
                        disabled={loading}
                      />
                      {errors.organizationName && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.organizationName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="fullName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Administrator Name *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.fullName ? "border-red-500" : "border-gray-200"
                        }`}
                        placeholder="Enter administrator name"
                        disabled={loading}
                      />
                      {errors.fullName && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.fullName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="tenantName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Tenant
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="tenantName"
                          name="tenantName"
                          value={formData.tenantName}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Tenant name"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="mobileNumber"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.mobileNumber
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your mobile number"
                        disabled={loading}
                      />
                      {errors.mobileNumber && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Email address"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        Email cannot be changed. Contact your administrator if
                        needed.
                      </p>
                    </div>
                  </>
                ) : userType === "orguser" ? (
                  <>
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.firstName
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your first name"
                        disabled={loading}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.lastName ? "border-red-500" : "border-gray-200"
                        }`}
                        placeholder="Enter your last name"
                        disabled={loading}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="department"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Department
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="department"
                          name="department"
                          value={formData.department}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Department"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="supervisor"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Supervisor
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="supervisor"
                          name="supervisor"
                          value={formData.supervisor}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Supervisor name"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Email address"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        Email cannot be changed. Contact your administrator if
                        needed.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="mobileNumber"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.mobileNumber
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your mobile number"
                        disabled={loading}
                      />
                      {errors.mobileNumber && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label
                        htmlFor="fullName"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.fullName ? "border-red-500" : "border-gray-200"
                        }`}
                        placeholder="Enter your full name"
                        disabled={loading}
                      />
                      {errors.fullName && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.fullName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Email address"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        Email cannot be changed. Contact your administrator if
                        needed.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="mobileNumber"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#0fa9a6] ${
                          errors.mobileNumber
                            ? "border-red-500"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your mobile number"
                        disabled={loading}
                      />
                      {errors.mobileNumber && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="department"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Department
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="department"
                          name="department"
                          value={formData.department}
                          readOnly
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-md bg-[#F4F6F8] text-gray-600 cursor-not-allowed"
                          placeholder="Department"
                        />
                        <LockClosedIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
                <button
                  type="button"
                  onClick={handleClose}
                  className="bg-[#8497B0] text-white px-4 py-2 rounded-full hover:opacity-90"
                  disabled={loading}
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="bg-[#00A8A8] text-white px-4 py-2 rounded-full hover:opacity-90"
                  disabled={loading || Object.keys(errors).length > 0}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      SAVING...
                    </div>
                  ) : (
                    "UPDATE"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditProfile;
